import React, { useMemo, type JSX } from 'react';
import {
  type BuildsRankingsSectionTableAccessor,
  type BuildsRankingsSectionTableRow,
  type BuildsRankingsSectionViewModel,
} from '../../../../archon/helpers/getBuildsZonePageViewModels';
import VerticalContent from '../VerticalContent/VerticalContent';
import Body from '../Body/Body';
import Table, { type TableColumn, type TableProps } from '../Table/Table';
import {
  IconDefaultsContextProvider,
  iconSizeLarge,
} from '../../contexts/useIconDefaults';
import Heading from '../Heading/Heading';
import useTheme from '../../contexts/useTheme';
import EnhancedMarkdown from '../EnhancedMarkdown/EnhancedMarkdown';
import BuildsNoDataPlaceholder from '../BuildsNoDataPlaceholder/BuildsNoDataPlaceholder';
import { type CellProps } from 'react-table';
import useGame from '@resources/js/contexts/useGame';
import { type GameViewModel } from '@resources/js/typings/viewModels/gameViewModel';

type BuildsRankingsSectionProps = BuildsRankingsSectionViewModel & {
  LinkWrapper: (props: {
    children: React.ReactNode;
    href: string;
  }) => JSX.Element;
  limit?: number;
};

const iconDefaults = {
  actor: { size: iconSizeLarge, bordered: true },
  encounter: { size: iconSizeLarge, bordered: true },
};

export default function BuildsRankingsSection({
  title,
  description,
  table,
  LinkWrapper,
  limit,
}: BuildsRankingsSectionProps): JSX.Element {
  const theme = useTheme();
  const game = useGame();

  const columns = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(table?.columns ?? {}).map(([accessor, column]) => [
          accessor,
          {
            ...column,
            renderCell: getCellRenderer(LinkWrapper, game),
          } as TableColumn<BuildsRankingsSectionTableRow>,
        ]),
      ),
    [table?.columns, LinkWrapper, game],
  );

  const truncate: TableProps['truncate'] = {
    showMoreButton: { styles: theme.primaryButtonStyles },
  };

  if (limit) {
    truncate.limit = limit;
  }

  return (
    <VerticalContent className={'builds-spec-rankings-section'}>
      <Body>
        <Heading level={2}>{title}</Heading>
        <p className={'hide-on-compact'}>{description}</p>
      </Body>
      <IconDefaultsContextProvider overrides={iconDefaults}>
        {table ? (
          <Table {...table} columns={columns} truncate={truncate} />
        ) : (
          <BuildsNoDataPlaceholder />
        )}
      </IconDefaultsContextProvider>
    </VerticalContent>
  );
}

export const formatValue = (value: number) => {
  if (value < 1000) {
    return Math.floor(value);
  }

  const formatter = new Intl.NumberFormat('en-US', {
    notation: 'compact',
    minimumFractionDigits: 1,
    maximumFractionDigits: 2,
    minimumSignificantDigits: 3,
    maximumSignificantDigits: 3,
  });
  return formatter.format(value);
};

const formatPercent = (value: number) => {
  return (
    new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }).format(value * 100) + '%'
  );
};

const getCellRenderer =
  (
    LinkWrapper: (props: {
      children: React.ReactNode;
      href: string;
    }) => JSX.Element,
    game: GameViewModel,
  ) =>
  (cell: CellProps<BuildsRankingsSectionTableRow>) =>
    renderTableValue(
      cell.row.original,
      cell.column.id as BuildsRankingsSectionTableAccessor,
      LinkWrapper,
      game,
    );

const renderTableValue = (
  row: BuildsRankingsSectionTableRow,
  accessor: BuildsRankingsSectionTableAccessor,
  LinkWrapper: (props: {
    children: React.ReactNode;
    href: string;
  }) => JSX.Element,
  game: GameViewModel,
) => {
  const keystonePrefix = game.id === 7 ? '' : '+';
  switch (accessor) {
    case 'item': {
      return (
        <LinkWrapper href={row.itemPath}>
          <EnhancedMarkdown markdown={row.item} wrapped={false} />
        </LinkWrapper>
      );
    }
    case 'rdps':
      return row.rdps ? formatValue(row.rdps) : '-';
    case 'adps':
      return row.adps ? formatValue(row.adps) : '-';
    case 'cdps':
      return row.cdps ? formatValue(row.cdps) : '-';
    case 'dps':
      return row.dps ? formatValue(row.dps) : '-';
    case 'rhps':
      return row.rhps ? formatValue(row.rhps ?? 0) : '-';
    case 'hps':
      return row.hps ? formatValue(row.hps ?? 0) : '-';
    case 'maxKey':
      return row.maxKey ? keystonePrefix + row.maxKey : '-';
    case 'minKey':
      return row.minKey ? keystonePrefix + row.minKey : '-';
    case 'score':
      return Math.round((row.score ?? 0) * 10) / 10;
    case 'survivability':
      return Math.round((row.survivability ?? 0) * 10) / 10 + '%';
    case 'popularity':
      return formatPercent(row.popularity);
    case 'parses':
      return row.parses.toLocaleString('en-US');
    case 'bar':
      return row.bar && <EnhancedMarkdown markdown={row.bar} wrapped={false} />;
    case 'usage':
      return row.usage && formatPercent(row.usage);
    default:
      return row[accessor];
  }
};
